import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const TRUST = "Built by operators, not theorists. Decades running P&Ls and teams, plus AI systems in production.";

/* Reusable content helpers ------------------------------------------------ */

export function Lead({ children }) {
  return <p style={{ fontSize: '1.05rem', color: '#334155', lineHeight: 1.8, marginBottom: 20, maxWidth: 760 }}>{children}</p>;
}

export function SubHead({ children }) {
  return <h3 style={{ fontSize: '1.15rem', color: '#0A1628', margin: '28px 0 10px' }}>{children}</h3>;
}

export function Included({ items }) {
  return (
    <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 14, marginTop: 8 }}>
      {items.map(d => (
        <li key={d} style={{ display: 'flex', gap: 12, alignItems: 'flex-start', fontSize: '0.98rem', color: '#334155' }}>
          <span style={{ color: '#378ADD', fontWeight: 700, marginTop: 1 }}>✓</span>
          {d}
        </li>
      ))}
    </ul>
  );
}

export function Callout({ label, children }) {
  return (
    <div style={{ background: '#EBF4FD', borderRadius: 10, padding: '28px 32px', margin: '8px 0' }}>
      {label && <p className="section-label" style={{ marginBottom: 8 }}>{label}</p>}
      <p style={{ color: '#334155', fontSize: '1rem', lineHeight: 1.8 }}>{children}</p>
    </div>
  );
}

export function BuildItem({ name, children }) {
  return (
    <div style={{ padding: '22px 24px', borderRadius: 10, border: '1px solid #EBF4FD', background: '#fff' }}>
      <h4 style={{ fontSize: '1rem', color: '#0A1628', marginBottom: 6, fontWeight: 700 }}>{name}</h4>
      <p style={{ fontSize: '0.92rem' }}>{children}</p>
    </div>
  );
}

/* FAQ Item ----------------------------------------------------------------- */

function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ borderBottom: '1px solid #E2E8F0' }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          background: 'none', border: 'none', cursor: 'pointer', padding: '20px 0',
          textAlign: 'left', gap: 16,
        }}
      >
        <span style={{ fontWeight: 700, color: '#0A1628', fontSize: '0.97rem' }}>{q}</span>
        <span style={{ color: '#378ADD', fontSize: '1.2rem', fontWeight: 700, flexShrink: 0 }}>{open ? '−' : '+'}</span>
      </button>
      {open && (
        <p style={{ fontSize: '0.97rem', color: '#64748B', lineHeight: 1.75, paddingBottom: 20 }}>{a}</p>
      )}
    </div>
  );
}

/* Layout ------------------------------------------------------------------ */

export default function ServiceSubpage({ seo, hero, schema, children, faqs, prevNext }) {
  return (
    <>
      <Helmet>
        <title>{seo.title}</title>
        <meta name="description" content={seo.description} />
        <link rel="canonical" href={`https://sagientpartners.com${seo.path}`} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`https://sagientpartners.com${seo.path}`} />
        <meta property="og:title" content={seo.title} />
        <meta property="og:description" content={seo.description} />
        <meta property="og:image" content="https://sagientpartners.com/social_card.png" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={seo.title} />
        <meta name="twitter:description" content={seo.description} />
        <meta name="twitter:image" content="https://sagientpartners.com/social_card.png" />
        {schema && <script type="application/ld+json">{JSON.stringify(schema)}</script>}
      </Helmet>

      {/* Hero */}
      <section style={{ background: 'linear-gradient(135deg, #0C447C 0%, #0A1628 100%)', padding: '32px 0 36px', color: '#fff' }}>
        <div className="container">
          <Link to="/services" style={{ color: '#B5D4F4', fontSize: '0.9rem', fontWeight: 500 }}>← All Services</Link>
          <p className="section-label" style={{ color: '#B5D4F4', marginTop: 20 }}>{hero.tier}</p>
          <h1 style={{ color: '#fff', maxWidth: 780, marginBottom: 18, fontSize: 'clamp(1.7rem, 4vw, 2.6rem)' }}>{hero.headline}</h1>
          <p style={{ color: '#DCEBFA', fontSize: '1.15rem', maxWidth: 660, lineHeight: 1.7, marginBottom: 24 }}>{hero.subhead}</p>
          <p style={{ color: '#B5D4F4', fontSize: '0.95rem', fontWeight: 600 }}>{hero.meta}</p>
        </div>
      </section>

      {/* Body sections passed as children */}
      {children}

      {/* Pre-FAQ CTA */}
      <section style={{ background: '#EBF4FD', padding: '40px 0', textAlign: 'center' }}>
        <div className="container">
          <Link to="/contact" className="btn btn-primary" style={{ fontSize: '1rem' }}>{hero.cta || 'Book a Free Discovery Call →'}</Link>
        </div>
      </section>

      {/* FAQ */}
      {faqs && faqs.length > 0 && (
        <section className="section section--alt">
          <div className="container">
            <p className="section-label">FAQ</p>
            <h2 className="section-heading" style={{ marginBottom: 32 }}>Common Questions</h2>
            <div style={{ display: 'flex', flexDirection: 'column', maxWidth: 820 }}>
              {faqs.map(f => <FaqItem key={f.q} q={f.q} a={f.a} />)}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section style={{ background: 'linear-gradient(135deg, #378ADD, #0C447C)', padding: '72px 0', textAlign: 'center' }}>
        <div className="container">
          <h2 style={{ color: '#fff', marginBottom: 16 }}>{hero.ctaHeading || 'Ready to Talk?'}</h2>
          <p style={{ color: '#B5D4F4', fontSize: '1.05rem', maxWidth: 560, margin: '0 auto 32px' }}>{hero.ctaSub}</p>
          <Link to="/contact" className="btn btn-white" style={{ fontSize: '1rem' }}>Book a Free 30-Minute Discovery Call →</Link>
          {prevNext && (
            <div style={{ marginTop: 28, display: 'flex', gap: 20, justifyContent: 'center', flexWrap: 'wrap' }}>
              {prevNext.map(pn => (
                <Link key={pn.to} to={pn.to} style={{ color: '#DCEBFA', fontSize: '0.92rem', fontWeight: 500 }}>{pn.label}</Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </>
  );
}
